import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "../lib/axios";
import { Users, Package, ShoppingCart, DollarSign } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

const AnalyticsTab = () => {
	const [analyticsData, setAnalyticsData] = useState({
		users: 0,
		products: 0,
		totalSales: 0,
		totalRevenue: 0,
	});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState(null);
	const [dailySalesData, setDailySalesData] = useState([]);

	useEffect(() => {
		const fetchAnalyticsData = async () => {
			try {
				setError(null);
				const response = await axios.get("/analytics");
				console.log("Analytics response:", response.data);
				
				if (response.data.analyticsData) {
					setAnalyticsData(response.data.analyticsData);
				}
				
				if (response.data.dailySalesData) {
					setDailySalesData(response.data.dailySalesData);
				}
			} catch (error) {
				console.error("Error fetching analytics data:", error);
				
				if (error.response?.status === 401) {
					setError("אין לך הרשאה לצפות בנתוני הניתוח. אנא התחבר כמנהל.");
				} else if (error.response?.status === 403) {
					setError("אין לך הרשאות מנהל לצפות בנתוני הניתוח.");
				} else if (error.response?.status === 500) {
					setError("שגיאת שרת בטעינת נתוני הניתוח. אנא נסה שוב מאוחר יותר.");
				} else {
					setError("שגיאה בטעינת נתוני הניתוח. אנא נסה שוב מאוחר יותר.");
				}
			} finally {
				setIsLoading(false);
			}
		};

		fetchAnalyticsData();
	}, []);

	if (isLoading) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="text-emerald-400 text-lg">טוען נתוני ניתוח...</div>
			</div>
		);
	}

	if (error) {
		return (
			<div className="flex justify-center items-center h-64">
				<div className="text-red-400 text-lg text-center max-w-md">
					{error}
					<br />
					<button 
						onClick={() => window.location.reload()} 
						className="mt-4 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700"
					>
						נסה שוב
					</button>
				</div>
			</div>
		);
	}

	return (
		<div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'>
			<div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8'>
				<AnalyticsCard
					title='סה"כ משתמשים'
					value={analyticsData.users.toLocaleString()}
					icon={Users}
					color='from-emerald-500 to-teal-700'
				/>
				<AnalyticsCard
					title='סה"כ מוצרים'
					value={analyticsData.products.toLocaleString()}
					icon={Package}
					color='from-emerald-500 to-green-700'
				/>
				<AnalyticsCard
					title='סה"כ מכירות'
					value={analyticsData.totalSales.toLocaleString()}
					icon={ShoppingCart}
					color='from-emerald-500 to-cyan-700'
				/>
				<AnalyticsCard
					title='סה"כ הכנסות'
					value={`₪${analyticsData.totalRevenue.toLocaleString()}`}
					icon={DollarSign}
					color='from-emerald-500 to-lime-700'
				/>
			</div>
			<motion.div
				className='bg-gray-800/60 rounded-lg p-6 shadow-lg'
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.25 }}
			>
				{dailySalesData.length > 0 ? (
					<ResponsiveContainer width='100%' height={400}>
						<LineChart data={dailySalesData}>
							<CartesianGrid strokeDasharray='3 3' />
							<XAxis dataKey='name' stroke='#D1D5DB' />
							<YAxis yAxisId='left' stroke='#D1D5DB' />
							<YAxis yAxisId='right' orientation='right' stroke='#D1D5DB' />
							<Tooltip />
							<Legend />
							<Line
								yAxisId='left'
								type='monotone'
								dataKey='sales'
								stroke='#10B981'
								activeDot={{ r: 8 }}
								name='מכירות'
							/>
							<Line
								yAxisId='right'
								type='monotone'
								dataKey='revenue'
								stroke='#3B82F6'
								activeDot={{ r: 8 }}
								name='הכנסות'
							/>
						</LineChart>
					</ResponsiveContainer>
				) : (
					<div className="flex justify-center items-center h-64">
						<div className="text-gray-400 text-lg">אין נתוני מכירות להצגה</div>
					</div>
				)}
			</motion.div>
		</div>
	);
};
export default AnalyticsTab;

const AnalyticsCard = ({ title, value, icon: Icon, color }) => (
	<motion.div
		className={`bg-gray-800 rounded-lg p-6 shadow-lg overflow-hidden relative ${color}`}
		initial={{ opacity: 0, y: 20 }}
		animate={{ opacity: 1, y: 0 }}
		transition={{ duration: 0.5 }}
	>
		<div className='flex justify-between items-center'>
			<div className='z-10'>
				<p className='text-emerald-300 text-sm mb-1 font-semibold'>{title}</p>
				<h3 className='text-white text-3xl font-bold'>{value}</h3>
			</div>
		</div>
		<div className='absolute inset-0 bg-gradient-to-br from-emerald-600 to-emerald-900 opacity-30' />
		<div className='absolute -bottom-4 -right-4 text-emerald-800 opacity-50'>
			<Icon className='h-32 w-32' />
		</div>
	</motion.div>
);
